import * as React from 'react';
import { ForwardedRef, forwardRef, useMemo } from 'react';
import { DataRequestType, DataRequestVO } from '@mc/data-filter-react';
import { IData, VirtualDataRef } from '../virtual-data/VirtualData';
import { BaseTableProps, TableColumn } from '../table/TableProps';
import { BaseDataTableProps, DataTable } from './DataTable';
import { filterTableData, sortTableData } from './dataUtils';
import { FilterDef } from '../filter/Filters';
import { FilterType } from '../filter/types';
import { ElementOf, tuple } from '../common/props';

export const ClientFilterLikeTypes = tuple('start', 'contains', 'end');
export type ClientFilterLikeType = ElementOf<typeof ClientFilterLikeTypes>;
export const ClientFilterOperatorTypes = tuple('eq', 'like');
export type ClientFilterOperatorType = ElementOf<
  typeof ClientFilterOperatorTypes
>;

export type ClientFilterDef = {
  operator?: ClientFilterOperatorType;
  likeType?: ClientFilterLikeType;
} & FilterDef;

export type ClientFilter<FILTER> = {
  type?: FilterType;
  filterDefs: ClientFilterDef[];
  formFilter: FILTER;
};

type Props<DATA extends IData<ID>, ID, FILTER, DATA_FOOTER> = {
  /*
  Objekt v 'rows' musí obsahovat propertu 'id', nebo je třeba použít funkci 'getItemId'.
   */
  rows?: DATA[];
  clientFilter?: ClientFilter<FILTER>;
} & BaseDataTableProps<DATA, ID, any, DATA_FOOTER> &
  BaseTableProps<DATA>;

const ClientTableIn = <
  DATA extends IData<ID>,
  ID extends unknown = {},
  FILTER extends unknown = {},
  DATA_FOOTER extends object = {}
>(
  {
    rows,
    noVerticalScroll,
    clientFilter,
    ...rest
  }: Props<DATA, ID, FILTER, DATA_FOOTER>,
  ref: ForwardedRef<VirtualDataRef<ID>>
) => {
  // const [sort, setSort] = useState<DataSortVO[]>([]);

  // Filtrování záznamů na klientovi (pokud je předáno)
  const filteredRows = useMemo(() => {
    if (!clientFilter) {
      return rows;
    }

    return filterTableData(
      rows as any,
      clientFilter.type || 'default',
      clientFilter.formFilter,
      clientFilter.filterDefs
    );
  }, [
    rows,
    clientFilter?.type,
    clientFilter?.formFilter,
    clientFilter?.filterDefs,
  ]);

  // Seřazení záznamů na klientovi (pokud je předáno)
  const filteredSortedRows = useMemo(() => {
    let cols: TableColumn<DATA>[];
    if (rest.checkable) {
      // Pokud je checkable, přidáme jeden na začátek, aby nám seděly indexy pro sort
      cols = [{}, ...rest.columns];
    } else {
      cols = rest.columns;
    }
    return sortTableData(filteredRows, cols, rest.sort);
  }, [filteredRows, rest.sort, rest.columns]);

  const apiRequest = async (request: DataRequestVO<any>) => {
    switch (request.requestType) {
      case DataRequestType.DATA:
        if (request.idsRequest) {
          return {
            rows: rows
              ? rows.filter((x: any) => {
                  // @ts-ignore
                  return request.idsRequest?.ids.indexOf(getItemId(x)) >= 0;
                })
              : [],
            totalElements: rows?.length || 0,
          };
        }
    }
    return {
      rows: [],
      totalElements: 0,
      totalDataElements: 0,
    };
  };

  const getItemId = rest.getItemId || ((item: DATA) => item?.id);

  const ids = useMemo<ID[] | undefined>(() => {
    if (!filteredSortedRows) {
      return undefined;
    }

    // @ts-ignore
    const res: ID[] = filteredSortedRows.map((row) => getItemId(row));
    return res;
  }, [filteredSortedRows]);

  return (
    <DataTable<DATA, any, ID, DATA_FOOTER>
      noVerticalScroll={noVerticalScroll}
      totalElements={filteredSortedRows?.length}
      ids={ids}
      ref={ref}
      // @ts-ignore
      api={apiRequest}
      throttleTime={150}
      initialCache={rows}
      {...rest}
    />
  );
};

export const ClientTable = forwardRef(ClientTableIn) as <
  DATA extends IData<ID>,
  ID extends unknown = {},
  FILTER extends unknown = {},
  DATA_FOOTER extends object = {}
>(
  props: Props<DATA, ID, FILTER, DATA_FOOTER> & {
    ref?: React.ForwardedRef<VirtualDataRef<ID>>;
  }
) => ReturnType<typeof ClientTableIn>;
